import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { connect } from 'react-redux';

import Modal from '../Modal';
import CategorySelect from '../../CategorySelect';
import Dropdown from '../../Dropdown';

import { selectIsERPCategoriesIsUsage } from '../../../redux/selectors/categories';
import {
    getCategoryTree,
    getErpCategories
} from '../../../redux/methods/categories';
import {
    selectStoreCurrentLanguage,
    selectStoreAvailableIsoList
} from '../../../redux/selectors/storeLocale';

import { generateNonIdsOptionsForDropdown } from '../../../utils/components';

import styles from './index.module.scss';

const propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    caption: PropTypes.string,
    categorySelect: PropTypes.func,
    getCategoryTree: PropTypes.func,
    getErpCategories: PropTypes.func,
    categoryTree: PropTypes.arrayOf(Object),
    currentLanguage: PropTypes.string,
    allLanguages: PropTypes.array,
    useErpCategories: PropTypes.bool,
    showAll: PropTypes.bool
};

const CategorySelectModal = ({
    isOpen,
    setIsOpen,
    caption,
    categorySelect,
    getCategoryTree,
    getErpCategories,
    categoryTree,
    currentLanguage,
    allLanguages,
    useErpCategories,
    showAll = false
}) => {
    const intl = useIntl();
    const translations = intl.messages.sharedComponents.categorySelectModal;

    /*
     * Kategorie ERP nie są przechowywane w state.categories.categoryTree,
     * dlatego modal przechowuje je lokalnie.
     *
     * null oznacza: jeszcze nie pobrano.
     * [] oznacza: pobrano, ale drzewo jest puste.
     */
    const [erpCategoryTree, setErpCategoryTree] = useState(null);

    const [categoriesToDisplay, setCategoriesToDisplay] = useState([]);
    const [categoriesDisplayOption, setCategoriesToDisplayOption] =
        useState('all');

    /*
     * Gdy konfiguracja sklepu mówi, że używane są kategorie ERP,
     * źródłem modala jest drzewo ERP.
     *
     * W przeciwnym razie zachowujemy dotychczasowe drzewo sklepowe.
     */
    const effectiveCategoryTree = useErpCategories
        ? erpCategoryTree
        : categoryTree;

    const isEffectiveCategoryTreePresent =
        Array.isArray(effectiveCategoryTree) &&
        effectiveCategoryTree.length > 0;

    const categoryAllName = useMemo(
        () => (allLanguages || []).map(lang => ({
            iso: lang,
            value: 'Wszystko'
        })),
        [allLanguages]
    );

    const allCategory = useMemo(
        () => ({
            category: {
                id: 0,
                depth: 0,
                metatags: null,
                name: categoryAllName,
                orderIndex: 0,
                parentStatus: null,
                quantity: null,
                status: 1,
                statusErp: null
            },
            subItems: []
        }),
        [categoryAllName]
    );

    const categoryTreeWithAll = useMemo(() => {
        const tree = Array.isArray(effectiveCategoryTree)
            ? effectiveCategoryTree
            : [];

        return showAll
            ? [allCategory, ...tree]
            : tree;
    }, [effectiveCategoryTree, showAll, allCategory]);

    const categoriesDisplayOptions =
        generateNonIdsOptionsForDropdown(
            ['all', 'activeOnly'],
            translations.dropdownSelector.options
        );

    /*
     * Pobieramy tylko to źródło, którego modal rzeczywiście powinien używać.
     *
     * getErpCategories() zwraca rezultat getData(), dlatego właściwe drzewo
     * znajduje się w result.payload.categoryTree.
     */
    useEffect(() => {
        let isMounted = true;

        if (!isOpen) {
            return () => {
                isMounted = false;
            };
        }

        if (useErpCategories) {
            if (erpCategoryTree === null) {
                getErpCategories().then(result => {
                    if (isMounted) {
                        setErpCategoryTree(
                            result?.payload?.categoryTree ?? []
                        );
                    }
                });
            }
        } else if (!categoryTree || categoryTree.length === 0) {
            getCategoryTree();
        }

        return () => {
            isMounted = false;
        };
    }, [
        isOpen,
        useErpCategories,
        erpCategoryTree,
        categoryTree,
        getCategoryTree,
        getErpCategories
    ]);

    const filterActive = categories => {
        const filtered = categories.filter(
            ({ category }) => category.status === 1
        );

        return filtered.map(item => ({
            ...item,
            subItems:
                item.subItems && item.subItems.length > 0
                    ? filterActive(item.subItems)
                    : item.subItems
        }));
    };

    /*
     * Aktualizujemy widok po:
     * - pobraniu właściwego drzewa,
     * - przełączeniu all / activeOnly,
     * - zmianie showAll.
     */
    useEffect(() => {
        if (categoriesDisplayOption === 'activeOnly') {
            setCategoriesToDisplay(
                filterActive(categoryTreeWithAll)
            );
        } else {
            setCategoriesToDisplay(categoryTreeWithAll);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryTreeWithAll, categoriesDisplayOption]);

    const getTranslation = node => {
        for (const n in node) {
            if (
                node[n].iso.toLowerCase() ===
                currentLanguage.toLowerCase()
            ) {
                return node[n].value;
            }
        }

        return null;
    };

    const prepareCategories = (nodes, path, level) => {
        return nodes.map(n => {
            const translatedName = getTranslation(n.category.name);
            const translatedPath =
                (path === '' ? '' : `${path} => `) +
                translatedName;

            return {
                id: n.category.id,
                name: translatedName,
                expanded: false,
                selected: false,
                level,
                path: translatedPath,
                children:
                    n.subItems &&
                    Array.isArray(n.subItems) &&
                    n.subItems.length > 0
                        ? prepareCategories(
                            n.subItems,
                            translatedPath,
                            level + 1
                        )
                        : null
            };
        });
    };

    let selectedCategoryId = null;
    let selectedCategory = null;

    const handleCategoryChanged = (id, member) => {
        selectedCategoryId = id;
        selectedCategory = member;
    };

    const handleCategorySelect = (id, member) => {
        if (id !== null) {
            categorySelect(id, member);
            setIsOpen(false);
        }
    };

    const handleCategorySelectConfirm = () => {
        if (selectedCategoryId !== null) {
            categorySelect(selectedCategoryId, selectedCategory);
            setIsOpen(false);
        }
    };

    const preparedCategories = isEffectiveCategoryTreePresent
        ? prepareCategories(categoriesToDisplay, '', 0)
        : [];

    return isEffectiveCategoryTreePresent ? (
        <Modal
            className={clsx('text-center modal-lg')}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            header={translations.header}
            onConfirm={handleCategorySelectConfirm}
            body={
                <React.Fragment>
                    <div className={styles.controlsView}>
                        <span>
                            {translations.dropdownSelector.label}
                        </span>

                        <Dropdown
                            options={categoriesDisplayOptions}
                            currentSelectionLabel={
                                translations.dropdownSelector.options[
                                    categoriesDisplayOption
                                ]
                            }
                            performSelection={
                                setCategoriesToDisplayOption
                            }
                        />
                    </div>

                    <span>
                        {caption || translations.caption}
                    </span>

                    <div className={clsx('mx-auto my-3')}>
                        <CategorySelect
                            categories={preparedCategories}
                            handleSelectionChanged={
                                handleCategoryChanged
                            }
                            handleSelectionConfirmed={
                                handleCategorySelect
                            }
                        />
                    </div>
                </React.Fragment>
            }
            buttons={{
                confirm: {
                    label: translations.selectButtonLabel,
                    color: 'primary'
                },
                cancel: {
                    label: translations.cancelButtonLabel,
                    color: 'secondary'
                }
            }}
        />
    ) : null;
};

CategorySelectModal.propTypes = propTypes;

const mapStateToProps = state => ({
    categoryTree: state.categories.categoryTree,
    currentLanguage: selectStoreCurrentLanguage(state),
    allLanguages: selectStoreAvailableIsoList(state),
    useErpCategories: selectIsERPCategoriesIsUsage(state)
});

const mapDispatch = {
    getCategoryTree,
    getErpCategories
};

export default connect(
    mapStateToProps,
    mapDispatch
)(CategorySelectModal);